import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, Users, MapPin, Phone, Mail, CheckCircle, ArrowRight,
  ArrowUpRight, FileCheck, ClipboardList, HardHat, Factory, Wrench,
  Cog, Briefcase, Hammer, Truck, Building2, Globe2, Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

/**
 * /poland-recruitment-agency-india
 * ────────────────────────────────────────────────────────────────────────
 * Poland-corridor B2B recruitment landing page targeting Polish employers.
 * Primary keyword: "Poland Recruitment Agency in India".
 * Research-anchored against 2026 labour-market data: Type A work-permit,
 * PLN 4,806/month statutory minimum wage, ~1.29M foreign workers, India
 * among fastest-growing origin countries, logistics/warehousing #1 demand.
 */

const PRIMARY_PHONE = '+91-99588-48998';
const PRIMARY_EMAIL = 'info@zafco.in';
const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';

const ADVANTAGES = [
  { label: 'MEA Licensed Recruitment Agency', detail: 'Government of India approved with full MEA registration and regulatory compliance' },
  { label: '35+ Years Experience', detail: 'Three-and-a-half decades of skilled-manpower deployment across EU, Gulf and Eastern European corridors' },
  { label: 'Poland Recruitment Specialists', detail: 'Dedicated Poland desk fluent in voivodeship-level work-permit procedures and 2026 Polish labour code' },
  { label: 'Voivodeship Work Permit Expertise', detail: 'Filing experience across Mazowieckie, Wielkopolskie, Śląskie, Małopolskie and Dolnośląskie voivodeships' },
  { label: 'Trade Testing Facilities', detail: 'In-house competency verification for welders, fitters, CNC operators, forklift drivers and skilled trades' },
  { label: 'Pan-India Recruitment Network', detail: 'Sourcing centres across 12+ states giving the widest skilled-labour pool in India' },
  { label: 'Fast Mobilization', detail: '4–8 week mobilization window from signed requirement to deployment, depending on voivodeship permit timelines' },
  { label: 'Multi-Stage Candidate Screening', detail: 'CV vetting, technical interview, trade test, English-language screen, medical and client interview' },
  { label: 'Documentation Support', detail: 'Full assistance with work-permit application, employment contract, apostille and ZUS-compliance documents' },
  { label: 'EU-Compliant Visa Processing', detail: 'National Visa (Type D) processing through the Polish Embassy in Delhi with end-to-end candidate handholding' },
];

const INDUSTRY_GROUPS = [
  {
    icon: HardHat,
    title: 'Construction & Infrastructure',
    intro: "Poland's construction sector — residential, commercial, infrastructure and EU-funded projects — runs a structural shortage of skilled trades. ZAFCO supplies:",
    roles: [
      'Mason (bricklayer / block mason) — residential & commercial structural work',
      'Carpenter (joiner / formworker) — structural and finish carpentry',
      'Steel Fixer (rebar worker) — reinforcement layout and binding',
      'Shuttering Carpenter — concrete formwork and scaffold carpentry',
      'Painter (decorator) — interior, exterior, surface preparation',
      'Scaffolder — scaffolding assembly, dismantling and safety protocols',
      'Electrician — building wiring and electrical-distribution systems',
      'Plumber (pipefitter) — water supply, drainage and HVAC piping',
    ],
  },
  {
    icon: Factory,
    title: 'Manufacturing Workforce',
    intro: 'Polish manufacturing — automotive parts, electronics, white goods, food processing, plastics — depends on foreign skilled labour for line and operator roles:',
    roles: [
      'CNC Operator — computer-numerical-control machining and tooling',
      'Machine Operator — lathe, milling, universal-machine operation',
      'Production Worker — assembly-line and mass-production environments',
      'Assembly Technician — mechanical and electronic sub-assembly',
      'Maintenance Technician — preventive and breakdown plant maintenance',
    ],
  },
  {
    icon: Package,
    title: 'Logistics & Warehousing Workforce',
    intro: "Logistics and warehousing is Poland's most acute foreign-labour shortage in 2026 — driven by Poland's role as Europe's e-commerce fulfilment hub and the Amazon, Zalando, DHL and Hermes distribution centres clustered around Warsaw, Poznań and Wrocław. This is ZAFCO's highest-volume Poland category:",
    roles: [
      'Warehouse Operator — inbound goods, putaway, stock-rotation discipline',
      'Order Picker — voice-pick, RF-scanner and pick-to-light systems',
      'Packer — outbound pack-bench, label, tape, palletise',
      'Forklift Operator — counterbalance, reach truck, VNA, EU forklift licence support',
      'Material Handler — cross-dock and dock-loading operations',
      'Inventory Assistant — cycle counts, stock reconciliation, WMS data entry',
      'Logistics Coordinator — shift coordination and inbound/outbound flow management',
    ],
  },
  {
    icon: Hammer,
    title: 'Welding & Fabrication',
    intro: 'Polish shipyards (Gdańsk, Szczecin), heavy-equipment manufacturers and EPC contractors hire certified welders and fabricators:',
    roles: [
      'MIG Welder — Metal Inert Gas welding for steel and aluminium',
      'TIG Welder — Tungsten Inert Gas welding for precision applications',
      'ARC Welder — electric-arc welding for structural and pipeline work',
      '6G Welder — pipe welding at 6G position (industry-standard certification)',
      'Fabricator (Welder-Fabricator) — metal fabrication and structural welding',
      'Pipe Fitter — pipeline alignment and welding preparation',
    ],
  },
  {
    icon: Briefcase,
    title: 'Engineering Professionals',
    intro: 'Polish EPC firms and OEMs hire Indian engineers for project management, design coordination and commissioning roles:',
    roles: [
      'Civil Engineer — B.E./B.Tech with infrastructure project experience',
      'Mechanical Engineer — process equipment, HVAC, manufacturing systems',
      'Electrical Engineer — power distribution, control systems, building services',
      'QA/QC Engineer — quality assurance and quality control protocols',
      'Project Engineer — PMP-qualified, multi-site project management',
    ],
  },
];

const PROCESS_STAGES = [
  { stage: '01', name: 'Requirement Analysis', timeline: '2–3 days' },
  { stage: '02', name: 'Candidate Sourcing', timeline: '5–7 days' },
  { stage: '03', name: 'CV Screening', timeline: '3–4 days' },
  { stage: '04', name: 'Trade Test', timeline: '4–5 days' },
  { stage: '05', name: 'Client Interview', timeline: '3–5 days' },
  { stage: '06', name: 'Medical Examination', timeline: '2–3 days' },
  { stage: '07', name: 'Work Permit Filing', timeline: '4–10 weeks' },
  { stage: '08', name: 'National Visa (Type D)', timeline: '2–4 weeks' },
  { stage: '09', name: 'Mobilization', timeline: '5–7 days' },
];

const INDIA_ADVANTAGES = [
  { factor: 'Technical Competency', impact: "India's engineering education system produces workers familiar with EU safety standards, EN codes and modern manufacturing protocols." },
  { factor: 'English Communication', impact: 'Most skilled Indian workers operate in English — directly aligned with Polish employers using English as their plant-floor working language.' },
  { factor: 'Productivity', impact: 'Documented Indian-worker productivity in EU industrial settings is on par with or above EU-source labour at structurally lower employment cost.' },
  { factor: 'Cost Efficiency', impact: 'Indian sourcing typically delivers 25–35% lower fully-loaded workforce cost vs. Ukrainian, Belarusian or Western EU alternatives.' },
  { factor: 'Adaptability', impact: 'Track record of integrating into Polish, German and Czech industrial cultures within the first 60 days post-deployment.' },
  { factor: 'International Experience', impact: 'Many candidates carry prior project experience from the Gulf, Eastern Europe or Africa — already comfortable with international site discipline.' },
  { factor: 'Long-term Retention', impact: 'Lower attrition than the Eastern-European foreign-labour pool — Indian workers typically commit to 2–3 year deployment cycles.' },
];

const SERVICES = [
  { title: 'Permanent Recruitment', body: 'Full-time placement for core operational positions in manufacturing, logistics and engineering — long-term retention focus.' },
  { title: 'Bulk Hiring Solutions', body: 'Large-scale recruitment of 100–10,000 workers for warehouse openings, plant ramp-ups and project mobilizations — rolling weekly deployment schedules.' },
  { title: 'Project Recruitment', body: 'Temporary workforce for fixed-duration contracts (6 months to 3 years) — typical for construction projects and plant-commissioning windows.' },
  { title: 'Executive Search', body: 'Headhunting for senior professionals — plant directors, supply-chain heads, technical experts with 10+ years EU experience.' },
  { title: 'Skilled Trades Recruitment', body: 'Specialised placement of trade-tested welders, fitters, electricians, forklift operators and CNC machinists — with EN-code and 6G certification support.' },
  { title: 'Turnkey Recruitment Solutions', body: 'End-to-end responsibility from sourcing to deployment — work-permit filing, visa processing, travel, initial accommodation and 30-day post-arrival support.' },
];

const REGIONS = [
  { region: 'Delhi NCR', strengths: 'Manufacturing workers, electricians, machine operators, engineering graduates fluent in English' },
  { region: 'Uttar Pradesh', strengths: 'Construction workers (masons, carpenters, steel fixers), painters, plumbers — high-volume bulk sourcing' },
  { region: 'Bihar', strengths: 'Bulk construction workforce, warehouse operators, forklift candidates — strong for high-headcount logistics mandates' },
  { region: 'Rajasthan', strengths: 'Welders, fabricators, maintenance technicians, structural-steel specialists' },
  { region: 'Punjab', strengths: 'CNC operators, assembly technicians, mechanical technicians, mid-skill engineers' },
  { region: 'Haryana', strengths: 'Industrial workers, production staff, QA/QC engineers, project engineers' },
  { region: 'Jharkhand', strengths: 'Mining workers, pipe fitters, mechanical technicians, fabricators' },
  { region: 'Maharashtra', strengths: 'Engineering professionals, CNC operators, instrument technicians, engineers with EU-spec familiarity' },
  { region: 'Karnataka', strengths: 'Tech-savvy workers, electronic assembly, electrical engineers, QA engineers' },
  { region: 'Tamil Nadu', strengths: 'Manufacturing workers, assembly technicians, welders, machine operators (automotive-experienced)' },
];

const SECTOR_DEMAND = [
  { icon: Building2, title: 'Construction', body: 'EU-funded infrastructure programmes (Tarcza Wschodnia, KPO recovery projects), residential development and commercial real-estate continue to absorb foreign skilled trades across all major voivodeships.' },
  { icon: Factory, title: 'Manufacturing', body: 'Polish factories serve as the EU\u2019s industrial back-office — white goods, plastics, packaging and chemicals plants run continuous shift patterns requiring operator and technician headcount.' },
  { icon: Cog, title: 'Automotive', body: 'Volkswagen Poznań, Stellantis Tychy, Mercedes-Benz Jawor and 350+ Tier-1/Tier-2 automotive suppliers form one of the EU\u2019s densest auto-clusters — heavy ongoing demand for CNC, assembly and quality roles.' },
  { icon: Package, title: 'Food Processing', body: 'Poland is the EU\u2019s largest poultry producer and a top dairy exporter. Food-processing plants in Wielkopolska and Mazowieckie hire production workers, packers and HACCP-trained operators continuously.' },
  { icon: Truck, title: 'Logistics', body: 'Poland\u2019s position on the EU\u2019s east-west freight corridor and its emergence as the Amazon, DHL, Hermes and Zalando fulfilment hub drives the country\u2019s deepest labour gap — warehouse, picker, packer and forklift roles.' },
  { icon: Package, title: 'Warehousing', body: 'Cross-border e-commerce growth and the shift of European 3PL hubs to Polish ground (Łódź, Stryków, Bydgoszcz) creates sustained warehouse-operative demand across 24/7 shift patterns.' },
  { icon: Briefcase, title: 'Engineering', body: 'EPC contractors, plant builders, MEP firms and design consultancies hire Indian civil, mechanical, electrical and QA/QC engineers for both site-execution and design-back-office mandates.' },
  { icon: Wrench, title: 'Industrial Maintenance', body: 'Reliability-engineering and preventive-maintenance roles in food, automotive and chemicals plants face acute supply shortages — Indian mechanical and electrical technicians are a leading sourcing solution.' },
];

const FAQS = [
  { q: 'Do you recruit manpower for Poland from India?',
    a: 'Yes. ZAFCO is a licensed Poland Recruitment Agency in India sourcing skilled manpower for Polish employers across construction, manufacturing, logistics/warehousing, welding and engineering. Our Poland desk handles end-to-end mobilization including Type A work-permit filing and Type D National Visa processing.' },
  { q: 'What industries do you recruit for Poland?',
    a: 'Construction, manufacturing (automotive, food processing, white goods, plastics), logistics and warehousing (warehouse operators, pickers, packers, forklift drivers), welding/fabrication (MIG, TIG, ARC, 6G) and engineering (civil, mechanical, electrical, QA/QC, project).' },
  { q: 'Can you provide bulk manpower for Poland?',
    a: 'Yes. We operate bulk-hiring engagements of 100–10,000 workers with rolling weekly mobilization plans — typically activated by Polish 3PL operators, automotive Tier-1 plants and EPC contractors during expansion or seasonal-peak windows.' },
  { q: 'How long does recruitment take?',
    a: 'End-to-end timeline is typically 6–10 weeks from signed requirement to candidate landing in Poland. The work-permit filing is the longest single stage (4–10 weeks depending on voivodeship — Mazowieckie/Warsaw historically has the longest queues).' },
  { q: 'What documents are required for Poland employment?',
    a: 'A valid Indian passport (18+ months validity), Polish-employer signed employment contract, work-permit application package, police-clearance certificate, medical fitness certificate, trade-test certification for skilled trades, educational/qualification attestation with apostille, and the Type D National Visa application. ZAFCO compiles and submits the full dossier.' },
  { q: 'What is a Type A Work Permit?',
    a: 'A Type A Work Permit is the standard Polish work permit issued when an Indian worker is hired by an employer registered in Poland to work on Polish territory. It is filed by the employer at the relevant Voivode Office; from 2026 the statutory minimum wage that must be paid is PLN 4,806/month.' },
  { q: 'What is the Single Permit?',
    a: 'The Single Permit (zezwolenie jednolite) combines work and residence authorisation in one document. It is applied for by the foreign worker after arrival or by the employer on the worker\u2019s behalf, and is increasingly the preferred route as Poland digitalises its immigration processes.' },
  { q: 'What is an EU Blue Card?',
    a: 'The EU Blue Card is a work-and-residence permit for highly-qualified non-EU professionals (typically engineers, IT specialists, project managers) with a recognised university degree and a job offer paying above the regional reference threshold. It carries faster mobility rights across the EU after a qualifying period.' },
  { q: 'What is a National Visa Type D?',
    a: 'Type D is the long-stay (180+ days) entry visa stamped on the candidate\u2019s passport at the Polish Embassy in Delhi. Once a Type A or Single Permit is issued, Type D is the entry document used to fly into Poland. Processing is typically 2–4 weeks once the work permit is in hand.' },
  { q: 'How quickly can workers be mobilized to Poland?',
    a: 'For pre-screened standby talent pools — particularly in welding, warehouse and food-processing trades — mobilization can be compressed to 6–8 weeks. Bulk warehouse and 3PL mandates typically run on rolling weekly mobilization with 50–200 worker tranches.' },
  { q: 'Do Polish employers prefer English-speaking workers?',
    a: 'Yes. Most Polish industrial and logistics employers operate in English on the plant floor or in mixed Polish/English environments — a key advantage of Indian sourcing, since basic-to-intermediate English is standard for trade-tested Indian workers.' },
  { q: 'What is the cost advantage of Indian manpower for Poland?',
    a: 'Fully-loaded employment cost for Indian-sourced manpower is typically 25–35% lower than Ukrainian, Belarusian or Western-EU alternatives once recruitment fees, transit, accommodation, productivity and retention are factored in.' },
  { q: 'Are your workers EU-safety-standard certified?',
    a: 'All skilled trades are trade-tested before mobilization. Welders are certified against international procedure standards used by Polish EPC contractors. Forklift operators receive EU forklift-licence orientation pre-departure and complete the local Polish UDT certification post-arrival.' },
  { q: 'Do you recruit warehouse workers for Poland?',
    a: 'Yes. Warehouse recruitment is our largest single Poland category. We supply warehouse operators, order pickers, packers, forklift operators, material handlers, inventory assistants and logistics coordinators trained for voice-pick, RF-scanner and WMS-driven 3PL environments.' },
  { q: 'Do you recruit forklift operators for Poland?',
    a: 'Yes. We supply experienced counterbalance, reach-truck and VNA (Very Narrow Aisle) forklift operators. Pre-mobilization we conduct trade-test verification; post-arrival ZAFCO coordinates the Polish UDT forklift-licence conversion through the employer.' },
  { q: 'Do you recruit construction workers for Poland?',
    a: 'Yes. Construction recruitment for Poland covers masons, carpenters, steel fixers, shuttering carpenters, painters, scaffolders, plumbers and electricians — sourced through our pan-India network and trade-tested at regional centres before mobilization.' },
  { q: 'Who pays the recruitment fee — the candidate or the employer?',
    a: 'ZAFCO operates on a fully MEA-compliant employer-paid recruitment-fee model. Candidates pay zero recruitment fees, in line with the Indian Emigration Act and the Polish employer\u2019s ETI (ethical-recruitment) obligations.' },
  { q: 'How does ZAFCO ensure candidate quality?',
    a: 'Multi-stage screening: CV evaluation → technical-skills interview → English-language screen → in-person trade test → client video interview → medical examination → 30-day post-deployment performance monitoring through the Polish employer.' },
];

const PolandRecruitmentAgencyIndiaPage = () => {
  usePageMeta(
    'Poland Recruitment Agency in India for Skilled Manpower | ZAFCO',
    'MEA-licensed Poland Recruitment Agency in India. Skilled Indian manpower for Polish construction, manufacturing, logistics, warehousing & engineering. 35+ yrs.',
    {
      canonical: '/poland-recruitment-agency-india',
      image: '/social-media/og-poland-recruitment-agency-india.jpg',
      imageAlt: 'Poland Recruitment Agency in India — ZAFCO supplies skilled Indian manpower for Polish construction, manufacturing, logistics and warehousing employers.',
    },
  );

  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'International Recruitment',
          item: 'https://zafco.in/international-manpower-recruitment-europe-russia-israel-saudi-arabia' },
        { '@type': 'ListItem', position: 3, name: 'Poland Recruitment Agency in India',
          item: 'https://zafco.in/poland-recruitment-agency-india' },
      ],
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Poland Recruitment Agency in India for Skilled Manpower',
      description: 'MEA-licensed overseas recruitment service supplying skilled Indian manpower to Polish employers in construction, manufacturing, logistics, warehousing, welding and engineering.',
      serviceType: 'Overseas Manpower Recruitment',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: [
        { '@type': 'Country', name: 'Poland' },
        { '@type': 'Country', name: 'India' },
      ],
      audience: { '@type': 'Audience', audienceType: 'Polish employers seeking skilled Indian manpower' },
      url: 'https://zafco.in/poland-recruitment-agency-india',
    };
    const inject = (data, id) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-poland-page', id);
      el.text = JSON.stringify(data).replace(/<\/(script)/gi, '<\\/$1');
      document.head.appendChild(el);
      return el;
    };
    const els = [inject(breadcrumb, 'breadcrumb'), inject(faq, 'faq'), inject(service, 'service')];
    return () => { els.forEach((el) => el?.remove()); };
  }, []);

  const contactQuery = '?source=poland-recruitment-agency-india';

  return (
    <div data-testid="poland-recruitment-agency-india-page">
      <PageHeader
        title="Poland Recruitment Agency in India for Skilled Manpower"
        subtitle="MEA-licensed overseas recruitment partner — supplying skilled Indian workforce to Polish construction, manufacturing, logistics, warehousing and engineering employers."
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'International Recruitment', path: '/international-manpower-recruitment-europe-russia-israel-saudi-arabia' },
          { label: 'Poland Recruitment Agency in India', path: '/poland-recruitment-agency-india' },
        ]}
      />

      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Shield className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-bold text-lg">Government of India Approved Recruitment Agency</p>
              <p className="text-green-100 text-sm">MEA Registration Certificate: {MEA_REG}</p>
            </div>
            <Award className="w-8 h-8 flex-shrink-0 hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* HERO INTRO */}
      <section className="py-16 bg-white" aria-labelledby="poland-intro">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Poland Corridor · 2026</span>
          <h2 id="poland-intro" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Trusted Poland Recruitment Agency in India
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Poland ended 2025 with approximately <strong>1.29 million foreign workers</strong> on its labour rolls and is projected to require another half-million by end-2026 to keep pace with EU-funded infrastructure programmes, automotive plant expansion, food-processing growth and the country&rsquo;s emergence as Europe&rsquo;s e-commerce fulfilment hub. <Link to="/" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">ZAFCO Human Resource Management</Link> is your MEA-licensed <strong>Poland Recruitment Agency in India</strong>, connecting Polish employers with India&rsquo;s skilled, technically-competent and cost-efficient workforce.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            India is among the fastest-growing origin countries for Polish labour migration in 2026 — Indian workers are sought for their technical training, English communication, EU-standards familiarity, productivity discipline and demonstrated long-term retention. With <strong>35+ years</strong> of overseas recruitment experience, ZAFCO delivers end-to-end mobilization: requirement analysis, candidate sourcing, trade testing, Type A work-permit filing, Type D National Visa processing and deployment. This corridor sits inside our broader{' '}
            <Link to="/international-manpower-recruitment-europe-russia-israel-saudi-arabia" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">
              International Manpower Recruitment programme
            </Link>{' '}covering Saudi Arabia, Europe, Russia and Israel.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3 mt-8 text-gray-700">
            {[
              'MEA-licensed and ISO-certified — full Indian regulatory compliance',
              '50,000+ pre-screened candidate database across India',
              'Trade-testing facilities for EU-standard skills verification',
              'Pan-India recruitment network covering 12+ states',
              'Voivodeship-level expertise — Mazowieckie, Wielkopolskie, Śląskie, Małopolskie, Dolnośląskie',
              'Full Type A, Single Permit and EU Blue Card documentation support',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-3 mt-10">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="poland-hero-cta-request-manpower">
              <Link to={`/contact${contactQuery}-request-manpower`}>
                <ArrowRight className="w-4 h-4 mr-2" aria-hidden="true" /> Request Manpower
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-zafco-blue text-zafco-blue hover:bg-zafco-blue/5" data-testid="poland-hero-cta-submit-requirement">
              <Link to={`/contact${contactQuery}-submit-requirement`}>Submit Job Requirement</Link>
            </Button>
            <Button asChild variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10" data-testid="poland-hero-cta-contact">
              <a href={`mailto:${PRIMARY_EMAIL}?subject=Poland%20recruitment%20enquiry`}>
                <Mail className="w-4 h-4 mr-2" aria-hidden="true" /> Contact Recruitment Team
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* WHY POLAND SHORTAGE */}
      <section className="py-16 bg-slate-50 border-t border-slate-200" aria-labelledby="poland-shortage">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 id="poland-shortage" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Why Poland faces skilled workforce shortages
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Poland&rsquo;s labour gap is structural, not cyclical. Six concurrent forces are pulling foreign workforce demand to record highs in 2026:
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { title: 'Aging workforce', body: 'Poland\u2019s working-age population is projected to shrink by ~3.5 million by 2050. The over-60 segment is the country\u2019s only growing age cohort.' },
              { title: 'Persistently low unemployment', body: 'Polish unemployment has hovered near 5% for three years — practically zero in industrial voivodeships — meaning local labour pools are already saturated.' },
              { title: 'Manufacturing expansion', body: 'EU industrial reshoring, US/EU FDI inflows and the automotive sector\u2019s EV transition have triggered continuous plant expansion, especially in Wielkopolska, Śląsk and Dolnośląsk.' },
              { title: 'Logistics growth', body: 'Cross-border e-commerce, the Amazon/Zalando/Hermes/DHL hub effect, and Poland\u2019s east-west freight corridor position have created Europe\u2019s deepest warehouse-labour shortage.' },
              { title: 'Construction demand', body: 'EU KPO recovery funds, residential housing pressure and military-base infrastructure programmes drive sustained skilled-trades demand across all major voivodeships.' },
              { title: 'Foreign-worker dependency', body: 'Foreign labour rose to ~1.29M by end-2025. Government and industry both project sustained reliance through 2030 — with India explicitly named among priority origin countries.' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-amber-400 transition-colors">
                <h3 className="font-bold text-zafco-blue mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-6 italic">
            Source signals: official Polish foreign-worker registrations (end-2025), public EU labour-mobility reporting and ZAFCO&rsquo;s active engagement with Polish employers across 2025–26.
          </p>
        </div>
      </section>

      {/* WHY ZAFCO */}
      <section className="py-16 bg-white" aria-labelledby="why-zafco">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="why-zafco" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Why choose ZAFCO for Poland recruitment?
          </h2>
          <p className="text-gray-700 mb-10 max-w-3xl">
            ZAFCO is one of India&rsquo;s few MEA-licensed agencies running a dedicated, full-service Poland desk. Polish employers choose us for the ten operational reasons below.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ADVANTAGES.map((a, idx) => (
              <motion.div
                key={a.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.35, delay: idx * 0.04 }}
                className="bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-amber-300 transition-colors"
              >
                <h3 className="text-base font-bold text-zafco-blue mb-2">{a.label}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{a.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-16 bg-slate-50" aria-labelledby="industries-poland">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="industries-poland" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Industries we recruit for Poland
          </h2>
          <p className="text-gray-700 mb-10 max-w-3xl">
            ZAFCO supplies specialised Indian manpower across Poland&rsquo;s shortage-hit sectors. Logistics and warehousing is the largest single category, followed by manufacturing and construction.
          </p>
          <div className="space-y-8">
            {INDUSTRY_GROUPS.map((group) => {
              const Icon = group.icon;
              return (
                <div key={group.title} className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="bg-zafco-blue text-white p-3 rounded-xl">
                      <Icon className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold text-zafco-blue">{group.title}</h3>
                      <p className="text-gray-700 mt-1">{group.intro}</p>
                    </div>
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-2 mt-4 text-gray-700">
                    {group.roles.map((role) => (
                      <li key={role} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span>{role}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="poland-industries-cta-submit">
              <Link to={`/contact${contactQuery}-submit-after-industries`}>
                <ClipboardList className="w-4 h-4 mr-2" aria-hidden="true" /> Submit Job Requirement
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* WORK PERMIT & VISA */}
      <section className="py-16 bg-white" aria-labelledby="poland-permit">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="poland-permit" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Poland work permit &amp; visa process
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8 max-w-3xl">
            Indian workers entering Poland follow one of four legal pathways. The right route depends on role type, qualification level and the employer&rsquo;s mobilization timeline. ZAFCO advises on the optimal pathway during requirement analysis.
          </p>
          <div className="grid md:grid-cols-2 gap-5 mb-10">
            {[
              { title: 'Type A Work Permit', body: 'The standard permit when an Indian worker is hired by a Polish-registered employer to work in Poland. Filed by the employer at the Voivode Office. From 2026, the statutory minimum wage payable to permit holders is PLN 4,806/month. Processing is typically 4–10 weeks depending on voivodeship.' },
              { title: 'Single Permit (zezwolenie jednolite)', body: 'Combines work and residence authorisation in one document. Increasingly the preferred route as Poland digitalises its immigration processes. Filed via the employer or by the worker after arrival.' },
              { title: 'EU Blue Card', body: 'For highly-qualified non-EU professionals (engineers, IT specialists, project managers) with a recognised university degree and a salary above the regional reference threshold. Carries faster intra-EU mobility rights after a qualifying period.' },
              { title: 'National Visa (Type D)', body: 'The long-stay (180+ days) entry visa stamped at the Polish Embassy in Delhi once a Type A or Single Permit is issued. Type D is the document used to enter Poland physically. Processing is typically 2–4 weeks.' },
            ].map((item) => (
              <div key={item.title} className="bg-slate-50 rounded-2xl p-6 border-l-4 border-amber-500">
                <h3 className="font-bold text-zafco-blue mb-2">{item.title}</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
          <h3 className="text-xl font-bold text-zafco-blue mb-4">Employer obligations &amp; compliance requirements</h3>
          <ul className="grid sm:grid-cols-2 gap-2 text-gray-700 text-sm mb-10">
            {[
              'Job offer issued on Polish-registered company letterhead',
              'Statutory minimum wage compliance (PLN 4,806/month, 2026)',
              'ZUS social-security registration within 7 days of arrival',
              'PIT income-tax withholding compliance',
              'Voivode Office work-permit filing with Voivode\u2019s consent',
              'PUP labour-market test (where applicable to role category)',
              'Accommodation provision or accommodation-allowance commitment',
              'Health & safety induction (BHP training) for arrived workers',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-bold text-zafco-blue mb-5">9-stage recruitment timeline</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PROCESS_STAGES.map((s) => (
              <div key={s.stage} className="bg-zafco-blue/5 border border-zafco-blue/20 rounded-xl p-4">
                <div className="text-amber-600 text-sm font-bold tracking-wider">{s.stage}</div>
                <div className="font-semibold text-zafco-blue mt-1">{s.name}</div>
                <div className="text-gray-600 text-xs mt-1">{s.timeline}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="bg-amber-500 text-slate-900 hover:bg-amber-400" data-testid="poland-process-cta-quote">
              <Link to={`/contact${contactQuery}-quote`}>
                <FileCheck className="w-4 h-4 mr-2" aria-hidden="true" /> Get Recruitment Quote
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* INDIA ADVANTAGES */}
      <section className="py-16 bg-slate-50" aria-labelledby="india-advantages">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="india-advantages" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Why Indian workers are preferred in Poland
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">
            India offers Polish employers a workforce that combines technical training, English communication and structurally lower employment cost — directly addressing the gaps left by the shrinking Ukrainian, Belarusian and Western-EU labour pools.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm md:text-base">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Advantage</th>
                  <th className="text-left px-5 py-3 font-semibold">Impact for Polish employers</th>
                </tr>
              </thead>
              <tbody>
                {INDIA_ADVANTAGES.map((row, idx) => (
                  <tr key={row.factor} className={idx % 2 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="px-5 py-3 font-medium text-zafco-blue">{row.factor}</td>
                    <td className="px-5 py-3 text-gray-700">{row.impact}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-16 bg-white" aria-labelledby="services-poland">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="services-poland" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Our recruitment services for Poland
          </h2>
          <p className="text-gray-700 mb-10 max-w-3xl">
            Six engagement models matched to Polish employer needs — from permanent core hires to large-scale 3PL warehouse mobilizations.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <div key={s.title} className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                <div className="bg-amber-100 text-amber-700 inline-flex p-2 rounded-lg mb-3">
                  <Wrench className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-zafco-blue mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDIA REGIONS */}
      <section className="py-16 bg-slate-50" aria-labelledby="india-regions">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="india-regions" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Recruitment locations across India
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">
            Pan-India sourcing across 10 priority states ensures access to the widest skilled-labour pool. Each region has dedicated recruitment centres with local trade-testing facilities.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm md:text-base">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Region</th>
                  <th className="text-left px-5 py-3 font-semibold">Sourcing strengths</th>
                </tr>
              </thead>
              <tbody>
                {REGIONS.map((row, idx) => (
                  <tr key={row.region} className={idx % 2 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="px-5 py-3 font-medium text-zafco-blue flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-600" aria-hidden="true" /> {row.region}
                    </td>
                    <td className="px-5 py-3 text-gray-700">{row.strengths}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SECTOR DEMAND AUTHORITY */}
      <section className="py-16 bg-white" aria-labelledby="sector-demand">
        <div className="container mx-auto px-6 max-w-6xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Sector Outlook · 2026</span>
          <h2 id="sector-demand" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Understanding Poland&rsquo;s workforce demand by sector
          </h2>
          <p className="text-gray-700 leading-relaxed mb-10 max-w-3xl">
            Eight sectors absorb the majority of foreign-skilled labour in Poland. ZAFCO calibrates its sourcing pipelines, trade-testing protocols and voivodeship work-permit strategies to the specific role mix each sector requires.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {SECTOR_DEMAND.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-amber-400 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-100 text-amber-700 p-2.5 rounded-xl flex-shrink-0">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-bold text-zafco-blue mb-1.5">{s.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-gray-700 leading-relaxed mt-8 text-sm">
            Future demand trends: independent EU labour-mobility data points to sustained shortages in warehousing, food processing and skilled-trades through 2030 — making Poland a multi-year corridor opportunity for Indian recruitment partners, not a single-cycle hiring market.
          </p>
        </div>
      </section>

      {/* WHY PARTNER + FINAL CTA */}
      <section className="py-16 bg-gradient-to-br from-zafco-blue to-slate-900 text-white" aria-labelledby="why-partner">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="why-partner" className="text-3xl md:text-4xl font-bold font-syne mb-3">
            Why partner with ZAFCO — your Poland Recruitment Agency in India
          </h2>
          <p className="text-slate-300 mb-8 max-w-3xl">
            Poland&rsquo;s labour gap is multi-year. The Polish employers winning the workforce-supply race in 2026 are those locking in dependable, MEA-licensed Indian partners now — not those scrambling each quarter.
          </p>
          <ul className="grid md:grid-cols-2 gap-3 mb-10">
            {[
              ['Recruitment expertise', '35+ years of overseas manpower supply with active Polish corridor experience'],
              ['Candidate quality', 'Multi-stage screening — trade test, English screen, client interview, medical, post-deployment monitoring'],
              ['Mobilization speed', '6–10 week end-to-end timeline from signed requirement to candidate landing in Poland'],
              ['Compliance', 'MEA-licensed, ISO-certified, employer-paid-fee model fully aligned with Polish ETI norms'],
              ['Client support', 'Dedicated Poland desk with 24/7 communication and single-point accountability'],
              ['EU corridor experience', 'Concurrent active mandates in Poland, Germany, Czechia, Slovakia and the Baltics'],
            ].map(([title, body]) => (
              <li key={title} className="flex items-start gap-3 bg-slate-800/40 p-4 rounded-xl border border-slate-700/60">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-semibold text-white">{title}</div>
                  <div className="text-slate-300 text-sm mt-0.5">{body}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to={`/contact${contactQuery}-final-employer`}
              className="bg-amber-500 text-slate-900 rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-amber-400 transition-colors"
              data-testid="poland-final-cta-employer"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-700">For Polish employers</span>
                <span className="text-lg">Start recruitment now</span>
              </span>
              <ArrowUpRight className="w-6 h-6" aria-hidden="true" />
            </Link>
            <a
              href={`tel:${PRIMARY_PHONE.replace(/[^+\d]/g, '')}`}
              className="bg-slate-800/60 border border-slate-700 text-white rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-slate-800 transition-colors"
              data-testid="poland-final-cta-phone"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-400">Speak to our Poland desk</span>
                <span className="text-lg">{PRIMARY_PHONE}</span>
              </span>
              <Phone className="w-6 h-6 text-amber-400" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section className="py-16 bg-white" aria-labelledby="poland-contact-form">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-8">
            <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Talk to our Poland desk</span>
            <h2 id="poland-contact-form" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
              Request manpower indent or sourcing brief
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Share your manpower requirement — role mix, headcount, voivodeship, deployment window. Our Poland desk responds within one working day with a sourcing plan and indicative timelines.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6 md:p-8 max-w-3xl mx-auto" data-theme="light">
            <ConsultationForm sourceTag="poland-recruitment-agency-india" />
          </div>
        </div>
      </section>

      {/* FAQS */}
      <section className="py-16 bg-slate-50" aria-labelledby="faqs-poland">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 id="faqs-poland" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Frequently asked questions
          </h2>
          <p className="text-gray-700 mb-8">
            Common questions Polish employers and Indian candidates ask about our Poland recruitment service.
          </p>
          <div className="space-y-4">
            {FAQS.map((f, idx) => (
              <details key={f.q} className="bg-white rounded-xl border border-slate-200 p-5 group" data-testid={`poland-faq-${idx}`}>
                <summary className="cursor-pointer font-semibold text-zafco-blue list-none flex items-start justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="text-amber-600 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="text-gray-700 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED RESOURCES */}
      <section className="py-12 bg-white border-t border-slate-200" aria-labelledby="related-resources">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="related-resources" className="text-xl font-bold text-zafco-blue mb-5">
            Related recruitment resources
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/international-manpower-recruitment-europe-russia-israel-saudi-arabia" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="poland-related-intl-pillar">
              <Globe2 className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">International Manpower Recruitment</div>
                <div className="text-xs text-gray-600 mt-0.5">Saudi · Europe · Russia · Israel</div>
              </div>
            </Link>
            <Link to="/russia-recruitment-agency-india" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="poland-related-russia">
              <Globe2 className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Russia Recruitment Agency</div>
                <div className="text-xs text-gray-600 mt-0.5">Sister corridor — same operating model</div>
              </div>
            </Link>
            <Link to="/eastern-europe-recruitment-india" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="poland-related-ee-hub">
              <Globe2 className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Eastern Europe Recruitment Hub</div>
                <div className="text-xs text-gray-600 mt-0.5">Multi-country comparison & coverage</div>
              </div>
            </Link>
            <Link to="/germany-opportunity-card" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="poland-related-germany">
              <Users className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Germany Opportunity Card</div>
                <div className="text-xs text-gray-600 mt-0.5">European corridor (visa pathway)</div>
              </div>
            </Link>
            <Link to="/careers" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="poland-related-careers">
              <Briefcase className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Active overseas openings</div>
                <div className="text-xs text-gray-600 mt-0.5">Submit your profile</div>
              </div>
            </Link>
            <Link to="/about" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="poland-related-about">
              <Cog className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">About ZAFCO HRM</div>
                <div className="text-xs text-gray-600 mt-0.5">35+ years overseas recruitment</div>
              </div>
            </Link>
            <Link to="/contact" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="poland-related-contact">
              <Mail className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Contact recruitment team</div>
                <div className="text-xs text-gray-600 mt-0.5">{PRIMARY_EMAIL}</div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PolandRecruitmentAgencyIndiaPage;
